// Header.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation, useResolvedPath } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFuncion } from '../context/FuncionContext';
import LogoProvincial from '../multimedia/Logo_Provicional.png';
import '../CSS/Header.css';

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

// Hook: isMobile con matchMedia
function useIsMobile(breakpoint = 768) {
  const mq = `(max-width: ${breakpoint}px)`;
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(mq).matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(mq);
    const onChange = (e) => setIsMobile(e.matches);
    try { mql.addEventListener("change", onChange); } catch { mql.addListener(onChange); }
    return () => {
      try { mql.removeEventListener("change", onChange); } catch { mql.removeListener(onChange); }
    };
  }, [mq]);
  return isMobile;
}


// Evita navegar si ya estás en la misma URL
function SafeNavLink({ to, className = '', end = false, onClick, children, ...rest }) {
  const location = useLocation();
  const resolved = useResolvedPath(to);
  const isSame =
    location.pathname === resolved.pathname &&
    location.search === resolved.search &&
    location.hash === resolved.hash;

  const handleClick = (e) => {
    if (isSame) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClick?.(e);
  };

  return (
    <NavLink
      to={to}
      end={end}
      onClick={handleClick}
      className={({ isActive }) => `${className} ${isActive ? 'active' : ''}`.trim()}
      {...rest}
    >
      {children}
    </NavLink>
  );
}

const Header = () => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isAdminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const isMobile = useIsMobile(768);
  const location = useLocation();

  // saber si estoy en /login
  const isLoginPage = location.pathname === "/login";

  const [courseFlags, setCourseFlags] = useState(null);
  const [courseFlagsReady, setCourseFlagsReady] = useState(false);
  const { user, logout, currentRole, switchRole } = useAuth();
  const { resolveStudentCourseByCode, getCourseProgress, toNum } = useFuncion();

  // === Helper: normalizar roles globales del usuario (sin docente/asistente) ===
  const getRawRoles = () => {
    if (!user?.roles) return [];
    return user.roles
      .map((r) => {
        if (typeof r === "string") return r;
        // soportar estructuras tipo { nombre: 'administrador', id: 1 } o { rol: 'docente' }
        return r.nombre || r.rol || r.role || "";
      })
      .filter(Boolean);
  };

  const hasFacultyAdmin = () => {
    const arr = user?.admin_facultades;
    if (!Array.isArray(arr) || arr.length === 0) return false;

    // solo si en alguna facultad tiene rol "admin"
    return arr.some(f =>
      Array.isArray(f?.roles) && f.roles.some(r => String(r).toLowerCase() === "admin")
    );
  };

  const hasFacultyCoordinator = () => {
    const arr = user?.admin_facultades;
    if (!Array.isArray(arr) || arr.length === 0) return false;

    return arr.some(f =>
      Array.isArray(f?.roles) && f.roles.some(r => String(r).toLowerCase() === "coordinador")
    );
  };

  // === Detectar roles por curso (curso_docentes) ===
  const hasCourseRole = (roleName) => {
    const target = (roleName || "").toLowerCase();

    // Formato objeto: user.course_roles = { docente: true, asistente: false }
    const courseRolesObj = user?.course_roles || user?.curso_roles || null;
    if (courseRolesObj && typeof courseRolesObj === "object" && !Array.isArray(courseRolesObj)) {
      if (courseRolesObj[target]) return true;
    }

    // Formato array: user.teacher_roles = ['docente', 'asistente']
    const teacherRolesArr = user?.teacher_roles || user?.roles_docente || null;
    if (Array.isArray(teacherRolesArr)) {
      if (teacherRolesArr.some((r) => String(r).toLowerCase() === target)) {
        return true;
      }
    }

    return false;
  };

  // === Construir lista de roles disponibles para el selector (globales + por curso) ===
  const getAvailableRoles = () => {
    const baseRoles = getRawRoles();

    // 1) Globales: administrador / estudiante (ignoramos docente/asistente aquí porque ya no son globales)
    const globalRoles = [];
    baseRoles.forEach((r) => {
      const low = String(r).toLowerCase();
      if (low === "docente" || low === "asistente") return;
      globalRoles.push(low);
    });

    const rolesSet = new Set(globalRoles); // lower-case

    // 2) Añadir rol docente si tiene al menos un curso como docente
    if (hasCourseRole("docente")) {
      rolesSet.add("docente");
    }

    // 3) Añadir rol asistente si tiene al menos un curso como asistente
    if (hasCourseRole("asistente")) {
      rolesSet.add("asistente");
    }

    // 4) Añadir rol admin_facultad si tiene al menos una facultad asignada
    if (hasFacultyAdmin()) {
      rolesSet.add("admin_facultad");
    }

    if (!hasFacultyAdmin() && hasFacultyCoordinator()) {
      rolesSet.add("coordinador_facultad");
    }

    return Array.from(rolesSet); // ['administrador', 'estudiante', 'docente', 'asistente', ...]
  };

  const availableRoles = getAvailableRoles();

  const dropdownRef = useRef(null);
  const avatarRef = useRef(null);
  const adminDropdownRef = useRef(null);

  const toggleDropdown = () => setDropdownOpen(v => !v);
  const toggleMenu = () => setMenuOpen(v => !v);
  const toggleAdminDropdown = () => setAdminDropdownOpen(v => !v);

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
    setMenuOpen(false);
  };

  // roleName siempre será un string en minúsculas ('administrador', 'estudiante', 'docente', 'asistente')
  const handleRoleChange = (_fakeId, roleName) => {
    if (switchRole) switchRole(_fakeId, roleName);
    window.dispatchEvent(new Event('roleChanged'));
    setDropdownOpen(false);
    setAdminDropdownOpen(false); // ✅
    setMenuOpen(false);          // ✅
  };

  useEffect(() => {
    if (!isAdmin()) {
      setAdminDropdownOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRole]);


  const handleLinkClick = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
    setAdminDropdownOpen(false);
  };

  const getUserName = () => {
    if (user?.nombres && user?.apellidos) return `${user.apellidos} ${user.nombres}`;
    if (user?.email) return user.email;
    return 'Usuario';
  };

  const getCurrentRoleName = () => {
    if (currentRole) return currentRole;
    // si no hay currentRole en contexto, usamos el primero disponible
    if (availableRoles.length > 0) return availableRoles[0];
    return "";
  };

  const isFacultyAdminRole = () => {
    const roleName = (getCurrentRoleName() || "").toLowerCase();
    return roleName === "admin_facultad";
  };

  const isGlobalAdminRole = () => {
    const roleName = (getCurrentRoleName() || "").toLowerCase();
    return roleName === "administrador" || roleName === "admin" || roleName === "superadmin";
  };

  // Tu isAdmin() debe permitir ambos
  const isAdmin = () => isGlobalAdminRole() || isFacultyAdminRole();

  const getRoleColor = (roleName) => {
    const roleColors = {
      administrador: '#FF9800',
      docente: '#4CAF50',
      asistente: '#8BC34A',
      estudiante: '#2196F3',
      admin_facultad: '#0b49f5',
    };
    return roleColors[(roleName || '').toLowerCase()] || '#6c757d';
  };

  const shouldShowRoleSelector = () => {
    // ahora se basa en availableRoles, ya filtrados (globales + por curso)
    return availableRoles.length > 1;
  };

  const adminMenuItems = (() => {
    const items = [{ path: "/admin", icon: "📊", label: "Dashboard" }];

    // ✅ Admin Global: Gestionar Facultades (NO cursos directo desde header)
    if (isGlobalAdminRole()) {
      items.push({ path: "/admin/facultades", icon: "🏛️", label: "Gestionar Facultades" });
    }

    // ✅ Admin Facultad: Gestionar Cursos (NO facultades)
    if (isFacultyAdminRole()) {
      items.push({ path: "/admin/cursos", icon: "📚", label: "Gestionar Cursos" });
    }

    // Comunes a ambos
    items.push(
      { path: "/admin/usuarios", icon: "👥", label: "Gestionar Usuarios" },
      { path: "/admin/examenes", icon: "📄", label: "Gestión de Exámenes" },
      { path: "/admin/tienda", icon: "🏪", label: "Gestionar Tienda" },
      { divider: true },
      { path: "/admin/reportes", icon: "📈", label: "Reportes" },
      { path: "/admin/configuracion", icon: "⚙️", label: "Configuración" }
    );

    return items;
  })();

  // Clic fuera de dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        avatarRef.current && !avatarRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target)) {
        setAdminDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Bloquear scroll body con menú móvil abierto
  useEffect(() => {
    if (!isMobile) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = isMenuOpen ? 'hidden' : original || '';
    return () => { document.body.style.overflow = original || ''; };
  }, [isMenuOpen, isMobile]);

  const toHttps = (url) =>
    typeof url === "string" ? url.replace(/^http:\/\//i, "https://") : url;

  const getAvatarSrc = () => {
    const a = user?.avatar;
    if (!a) return null;

    // Si viene como objeto { url }
    if (typeof a === "object" && a.url) {
      return a.url;
    }

    // URLs absolutas válidas
    if (
      typeof a === "string" &&
      (/^https?:\/\//i.test(a) ||
        a.startsWith("data:") ||
        a.startsWith("/"))
    ) {
      return a;
    }

    const raw = String(a).trim();
    const hasExt = /\.(png|jpg|jpeg|webp|gif)$/i.test(raw);
    const idOrName = encodeURIComponent(
      hasExt ? raw.replace(/\.(png|jpg|jpeg|webp|gif)$/i, "") : raw
    );
    const ext = hasExt ? raw.match(/\.(png|jpg|jpeg|webp|gif)$/i)[0] : ".png";
    const cacheBuster = user?.updated_at
      ? `?t=${encodeURIComponent(user.updated_at)}`
      : "";

    return `${API_BASE}/uploads/perfiles/${idOrName}${ext}${cacheBuster}`;
  };

  const getInitials = () => {
    const base = getUserName();
    const parts = String(base).trim().split(/\s+/).slice(0, 2);
    return parts.map(p => p[0]?.toUpperCase() || "").join("") || (user?.email?.[0]?.toUpperCase() || "U");
  };

  // Rutas según curso
  const pathParts = location.pathname.split('/').filter(Boolean);
  const inCourse = pathParts[0] === 'curso' && !!pathParts[1];
  const currentCodigoCurso = inCourse ? pathParts[1] : null;

  // Siempre mantener "Inicio" (/) y además "Autoaprendizaje" cuando estés dentro del curso
  const inicioHref = '/';
  const autoaprendizajeHref = inCourse ? `/curso/${currentCodigoCurso}` : '/';

  const helpHref = inCourse ? `/curso/${currentCodigoCurso}/ayuda` : '/ayuda';

  const loadCourseFlags = useCallback(() => {
    if (!inCourse || !currentCodigoCurso) {
      setCourseFlags(null);
      setCourseFlagsReady(true);
      return null;
    }

    const state = location.state;
    if (state && (state.examEligible != null || state.completedCourse != null)) {
      const flags = {
        examEligible: Boolean(state.examEligible),
        completedCourse: Boolean(state.completedCourse),
        minRequired: state.minRequired,
        progress: state.progress,
      };
      setCourseFlags(flags);
      try {
        sessionStorage.setItem(`courseFlags:${currentCodigoCurso}`, JSON.stringify(flags));
      } catch { }
      return flags;
    }

    try {
      const raw = sessionStorage.getItem(`courseFlags:${currentCodigoCurso}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        setCourseFlags(parsed);
        return parsed;
      }
    } catch { }

    setCourseFlags(null);
    return null;
  }, [inCourse, currentCodigoCurso, location.state]);

  const refreshCourseFlagsLive = useCallback(async () => {
    if (!inCourse || !currentCodigoCurso) {
      setCourseFlags(null);
      setCourseFlagsReady(true);
      return;
    }

    try {
      const course = await resolveStudentCourseByCode?.(currentCodigoCurso);
      if (!course?.id) {
        setCourseFlags(null);
        setCourseFlagsReady(true);
        return;
      }

      const rawMin =
        course.porcentaje_minimo_examen ??
        course.porcentaje_minimo_aprobacion ??
        100;

      const min =
        typeof toNum === "function"
          ? Math.max(0, Math.min(100, toNum(rawMin, 100)))
          : Math.max(0, Math.min(100, Number(rawMin) || 100));

      const progress = Number(getCourseProgress?.(course.id) || 0);
      const isExamOnly = String(course?.tipo || "").toLowerCase() === "examen_solo";
      const enrollmentStatus = course?.estado;
      const finalGrade = course?.nota_final != null ? Number(course.nota_final) : null;

      const eligible =
        isExamOnly ||
        enrollmentStatus === "completado" ||
        (finalGrade != null ? finalGrade >= min : progress >= min);

      const flags = {
        examEligible: !!eligible,
        completedCourse: !!eligible,
        minRequired: min,
        progress,
      };

      setCourseFlags(flags);

      try {
        sessionStorage.setItem(
          `courseFlags:${currentCodigoCurso}`,
          JSON.stringify(flags)
        );
      } catch { }
    } catch {
      setCourseFlags(null);
    } finally {
      setCourseFlagsReady(true);
    }
  }, [inCourse, currentCodigoCurso, resolveStudentCourseByCode, getCourseProgress, toNum]);
  // Flags de curso
  // Flags de curso
  useEffect(() => {
    setCourseFlagsReady(false);
    loadCourseFlags();
    refreshCourseFlagsLive();
  }, [location.pathname, location.key]);


  useEffect(() => {
    const handleCourseFlagsUpdated = (e) => {
      const code = e?.detail?.codigoCurso;
      if (!code || code !== currentCodigoCurso) return;

      const flags = e?.detail?.flags;
      if (flags) {
        setCourseFlags(flags);
        try {
          sessionStorage.setItem(`courseFlags:${currentCodigoCurso}`, JSON.stringify(flags));
        } catch { }
        return;
      }

      loadCourseFlags();
      refreshCourseFlagsLive();
    };

    const handleStorage = (e) => {
      if (!currentCodigoCurso) return;
      if (e.key === `courseFlags:${currentCodigoCurso}`) {
        loadCourseFlags();
      }
    };

    window.addEventListener("courseFlagsUpdated", handleCourseFlagsUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("courseFlagsUpdated", handleCourseFlagsUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [currentCodigoCurso, loadCourseFlags, refreshCourseFlagsLive]);

  const navAlign = 'left';

  const avatarNode = (() => {
    const src = getAvatarSrc();
    if (src) {
      return (
        <img
          ref={avatarRef}
          src={src}
          alt="Perfil"
          className="avatar"
          onClick={toggleDropdown}
          aria-haspopup="menu"
          aria-expanded={isDropdownOpen}
        />
      );
    }
    return (
      <button
        ref={avatarRef}
        className="avatar avatar-fallback"
        onClick={toggleDropdown}
        aria-haspopup="menu"
        aria-expanded={isDropdownOpen}
        title={getUserName()}
      >
        {getInitials()}
      </button>
    );
  })();

  const prettyRoleName = (r) => {
    const low = String(r || "").toLowerCase();
    if (low === "admin_facultad") return "Admin Facultad";
    return String(r).charAt(0).toUpperCase() + String(r).slice(1).toLowerCase();
  };

  return (
    <header className="header">
      {/* Overlay móvil debajo del menú */}
      {isMobile && (
        <div
          className={`nav-overlay ${isMenuOpen ? "show" : ""}`}
          onClick={() => setMenuOpen(false)}
          aria-hidden={!isMenuOpen}
        />
      )}

      <nav className="nav">
        <div className="logo">
          <SafeNavLink to="/" onClick={handleLinkClick}>
            <img src={LogoProvincial} alt="Logo Universidad" className="university-logo" />
          </SafeNavLink>
        </div>

        <div
          id="primary-navigation"
          className={`nav-links ${isMobile ? 'mobile' : 'desktop'} ${isMenuOpen ? 'show' : ''} align-${navAlign}`}
        >
          {/* Inicio (excepto /login) */}
          {!isLoginPage && (
            <SafeNavLink to={inicioHref} end onClick={handleLinkClick} className="pill-link">
              <span className="pill-label">Inicio</span>
              <span className="pill-underline" />
            </SafeNavLink>
          )}

          {/*{user && (
            <SafeNavLink
              to={inCourse ? `/curso/${currentCodigoCurso}/store` : "/store"}
              onClick={handleLinkClick}
              className="pill-link"
            >
              <span className="pill-label">UTMCoins</span>
              <span className="pill-underline" />
            </SafeNavLink>
          )}*/}

          <SafeNavLink to={helpHref || "/ayuda"} onClick={handleLinkClick} className="pill-link">
            <span className="pill-label">Ayuda</span>
            <span className="pill-underline" />
          </SafeNavLink>

          {/* Autoaprendizaje cuando estés dentro del curso */}
          {!isLoginPage && inCourse && (
            <SafeNavLink to={autoaprendizajeHref} end onClick={handleLinkClick} className="pill-link">
              <span className="pill-label">Autoaprendizaje</span>
              <span className="pill-underline" />
            </SafeNavLink>
          )}

          {user && (
            <>
              {inCourse && courseFlagsReady && courseFlags?.examEligible && (
                <SafeNavLink
                  to={`/curso/${currentCodigoCurso}/examen`}
                  onClick={handleLinkClick}
                  className="pill-link"
                >
                  <span className="pill-label">Examen</span>
                  <span className="pill-underline" />
                </SafeNavLink>
              )}

              {isAdmin() && (
                <div className="admin-nav-dropdown" ref={adminDropdownRef}>
                  <button
                    className={`admin-nav-button ${isAdminDropdownOpen ? 'active' : ''}`}
                    onClick={toggleAdminDropdown}
                    aria-expanded={isAdminDropdownOpen}
                    aria-controls="admin-dropdown-menu"
                  >
                    <span className="admin-icon">⚙️</span>
                    <span className="admin-text">Administración</span>
                    <span className={`dropdown-arrow ${isAdminDropdownOpen ? 'open' : ''}`}>▼</span>
                  </button>

                  {isAdminDropdownOpen && (
                    <div id="admin-dropdown-menu" className="admin-dropdown" role="menu">
                      <div className="admin-items">
                        {adminMenuItems.map((item, index) => {
                          if (item.divider) return <div key={`div-${index}`} className="admin-dropdown-divider" />;
                          return (
                            <SafeNavLink
                              key={item.path}
                              to={item.path}
                              onClick={() => { handleLinkClick(); setAdminDropdownOpen(false); }}
                              className="admin-dropdown-item"
                              end
                            >
                              <span className="admin-item-icon">{item.icon}</span>
                              <div className="admin-item-content">
                                <span className="admin-item-label">{item.label}</span>
                              </div>
                            </SafeNavLink>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* botón "Iniciar sesión" en menú móvil (si no hay user y no estamos en /login) */}
          {isMobile && !user && !isLoginPage && (
            <SafeNavLink to="/login" className="login-button-mobile" onClick={handleLinkClick}>
              Iniciar sesión
            </SafeNavLink>
          )}
        </div>

        <div className="header-right">
          {isMobile && (
            <button
              className={`hamburger ${isMenuOpen ? 'open' : ''}`}
              onClick={toggleMenu}
              aria-label="Abrir menú"
              aria-expanded={isMenuOpen}
              aria-controls="primary-navigation"
            >
              <span className="line" /><span className="line" /><span className="line" />
            </button>
          )}

          <div className="profile">
            {user ? (
              <>
                {!isMobile && (
                  <div className="user-info">
                    <div className="name">{getUserName()}</div>
                    {shouldShowRoleSelector() && (
                      <div className="current-role" style={{ color: getRoleColor(getCurrentRoleName()) }}>
                        {prettyRoleName(getCurrentRoleName())}
                      </div>
                    )}
                  </div>
                )}

                {avatarNode}

                {isDropdownOpen && (
                  <div ref={dropdownRef} className="dropdown">
                    <div className="dropdown-header">
                      {getAvatarSrc() ? (
                        <img src={getAvatarSrc()} alt="Perfil" className="dropdown-avatar" />
                      ) : (
                        <div className="dropdown-avatar dropdown-avatar-fallback">{getInitials()}</div>
                      )}
                      <div className="dropdown-info">
                        <div className="dropdown-name">{getUserName()}</div>
                        <div className="dropdown-email">{user?.email}</div>
                        <div className="current-role-display">
                          <span className="role-label">Rol activo:</span>
                          <span
                            className="current-role-badge"
                            style={{ backgroundColor: getRoleColor(getCurrentRoleName()), color: 'white' }}
                          >
                            {prettyRoleName(getCurrentRoleName())}
                          </span>
                        </div>
                      </div>
                    </div>

                    {shouldShowRoleSelector() && (
                      <div className="role-selector">
                        <div className="role-selector-header">
                          <span className="role-selector-icon">🔄</span>Cambiar rol
                        </div>
                        <div className="role-options">
                          {availableRoles.map((roleName, index) => {
                            const low = String(roleName).toLowerCase();
                            const isCurrentRole = low === getCurrentRoleName().toLowerCase();
                            const roleId = low + "-" + index;

                            return (
                              <button
                                key={roleId}
                                className={`role-option ${isCurrentRole ? 'active' : ''}`}
                                onClick={() => handleRoleChange(roleId, low)}
                                disabled={isCurrentRole}
                                style={{
                                  borderColor: isCurrentRole ? getRoleColor(low) : 'transparent',
                                  backgroundColor: isCurrentRole ? `${getRoleColor(low)}15` : 'transparent'
                                }}
                              >
                                <span
                                  className="role-indicator"
                                  style={{ backgroundColor: getRoleColor(low) }}
                                ></span>
                                <span className="role-name">{prettyRoleName(low)}</span>
                                {isCurrentRole && <span className="role-check">✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="dropdown-divider"></div>

                    <SafeNavLink to="/perfil" className="dropdown-button" onClick={() => setDropdownOpen(false)} end>
                      <span className="dropdown-icon">👤</span>Ver perfil
                    </SafeNavLink>

                    {isAdmin() && (
                      <SafeNavLink
                        to="/admin"
                        className="dropdown-button admin-button"
                        onClick={() => setDropdownOpen(false)}
                        end
                      >
                        <span className="dropdown-icon">📊</span>Dashboard Admin
                      </SafeNavLink>
                    )}

                    <button className="dropdown-button logout-button" onClick={handleLogout}>
                      <span className="dropdown-icon">🚪</span>Cerrar sesión
                    </button>
                  </div>
                )}
              </>
            ) : (
              (!isMobile && !isLoginPage) && (
                <SafeNavLink to="/login" className="login-button">
                  Iniciar sesión
                </SafeNavLink>
              )
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
